import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  togglePodcastBookmark,
  togglePodcastLike,
} from "../../services/podcastFeedbackService";
import {
  storePodcastProgress,
  storePodcastTime,
  togglePodcastReadStatus,
} from "../../services/podcastDetailService";
import VideoPlayer from '../lecture/VideoPlayer';
import "./AudioPlayer.css";

const formatTime = (value = 0) => {
  const seconds = Math.max(0, Math.floor(Number(value) || 0));
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
};

const AudioPlayer = ({
  podcast,
  subjectId,
  children,
  onOpenModal,
  onNavigateEpisode,
  onPurchase,
  onCoinUnlock,
}) => {
  const audioRef = useRef(null);
  const elapsedRef = useRef(0);
  const lastProgressRef = useRef(-1);
  const lastTimeRef = useRef(-1);
  const viewStoredRef = useRef(false);
  const restoredProgressRef = useRef(false);
  const videoProgressRef = useRef(Number(podcast?.progressTime || 0));
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(
    Number(podcast?.timerSeconds || 0),
  );
  const [isBookmarked, setIsBookmarked] = useState(
    Boolean(podcast?.isBookmarked),
  );
  const [isLiked, setIsLiked] = useState(Boolean(podcast?.isLiked));
  const [likeCount, setLikeCount] = useState(Number(podcast?.likeCount || 0));
  const [isRead, setIsRead] = useState(Boolean(podcast?.isRead));
  const [audioError, setAudioError] = useState(false);
  const locked =
    podcast?.freePaid === "P" && Number(podcast?.paymentStatus) !== 1;
  const canPlay = Boolean(podcast?.mediaUrl) && !locked;
  const provider = String(podcast?.videoSource || '').toLowerCase();
  const isVideo = provider === 'youtube' || provider === 'vimeo';
  const progress =
    duration > 0
      ? Math.min(100, Math.max(0, (currentTime / duration) * 100))
      : 0;

  useEffect(() => {
    setCurrentTime(0);
    setDuration(0);
    setElapsedSeconds(Number(podcast?.timerSeconds || 0));
    setIsBookmarked(Boolean(podcast?.isBookmarked));
    setIsLiked(Boolean(podcast?.isLiked));
    setLikeCount(Number(podcast?.likeCount || 0));
    setIsRead(Boolean(podcast?.isRead));
    setAudioError(false);
    lastProgressRef.current = -1;
    lastTimeRef.current = -1;
    viewStoredRef.current = false;
    restoredProgressRef.current = false;
  }, [
    podcast?.id,
    podcast?.isBookmarked,
    podcast?.isLiked,
    podcast?.isRead,
    podcast?.likeCount,
    podcast?.timerSeconds,
  ]);
  useEffect(() => {
    elapsedRef.current = elapsedSeconds;
  }, [elapsedSeconds]);
  useEffect(() => {
    if (!podcast?.id || locked) return undefined;
    const timer = window.setInterval(
      () => setElapsedSeconds((value) => value + 1),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [podcast?.id, locked]);
  const persistCurrentEpisode = useCallback(async () => {
    if (!podcast?.id || !subjectId || locked) return;

    const progressTime = Math.floor(audioRef.current?.currentTime ?? videoProgressRef.current);
    const timerSeconds = Math.max(1, Math.floor(elapsedRef.current));
    const requests = [];

    if (progressTime !== lastProgressRef.current) {
      lastProgressRef.current = progressTime;
      requests.push(
        storePodcastProgress({
          audio_podcast_id: podcast.id,
          subject_id: subjectId,
          video_seconds: progressTime,
        }).catch(() => {
          lastProgressRef.current = -1;
        }),
      );
    }

    if (timerSeconds !== lastTimeRef.current) {
      lastTimeRef.current = timerSeconds;
      requests.push(
        storePodcastTime({
          audio_podcast_id: podcast.id,
          subject_id: subjectId,
          timer_seconds: timerSeconds,
          is_view: 0,
        }).catch(() => {
          lastTimeRef.current = -1;
        }),
      );
    }

    await Promise.allSettled(requests);
  }, [podcast?.id, subjectId, locked]);
  useEffect(() => {
    if (!podcast?.id || !subjectId) return undefined;
    const persist = () => { void persistCurrentEpisode(); };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") persist();
    };
    window.addEventListener("pagehide", persist);
    window.addEventListener("popstate", persist);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      persist();
      window.removeEventListener("pagehide", persist);
      window.removeEventListener("popstate", persist);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [podcast?.id, subjectId, persistCurrentEpisode]);

  useEffect(() => {
    if (!isPlaying) return undefined;
    const progressTimer = window.setInterval(() => {
      void persistCurrentEpisode();
    }, 15000);
    return () => window.clearInterval(progressTimer);
  }, [isPlaying, persistCurrentEpisode]);

  const markRead = () => {
    if (locked || isRead || !podcast?.id || !subjectId) return;
    setIsRead(true);
    togglePodcastReadStatus({
      audio_podcast_id: podcast.id,
      subject_id: subjectId,
    }).catch(() => setIsRead(false));
  };
  const saveProgress = () => { void persistCurrentEpisode(); };
  const togglePlay = async () => {
    if (locked) return onPurchase?.();
    const audio = audioRef.current;
    if (!audio || !canPlay) return;
    try {
      if (audio.paused) await audio.play();
      else audio.pause();
    } catch (_error) {
      setAudioError(true);
    }
  };
  const navigateEpisode = async (episodeId) => {
    if (!episodeId) return;
    await persistCurrentEpisode();
    onNavigateEpisode?.(episodeId);
  };
  const seek = (event) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Number(event.target.value);
      setCurrentTime(audioRef.current.currentTime);
    }
  };
  const seekBy = (amount) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(
        0,
        Math.min(
          Number(audioRef.current.duration) || 0,
          audioRef.current.currentTime + amount,
        ),
      );
      setCurrentTime(audioRef.current.currentTime);
    }
  };
  const toggleBookmark = () => {
    const previous = isBookmarked;
    setIsBookmarked(!previous);
    togglePodcastBookmark({ audio_podcast_id: podcast.id }).catch(() =>
      setIsBookmarked(previous),
    );
  };
  const toggleLike = () => {
    const previous = isLiked;
    setIsLiked(!previous);
    setLikeCount((value) => Math.max(0, value + (previous ? -1 : 1)));
    togglePodcastLike({ audio_podcast_id: podcast.id })
      .then(({ payload }) => {
        if (payload?.like_status !== undefined)
          setIsLiked(Number(payload.like_status) === 1);
        if (payload?.total_liked !== undefined)
          setLikeCount(Number(payload.total_liked));
      })
      .catch(() => {
        setIsLiked(previous);
        setLikeCount((value) => Math.max(0, value + (previous ? 1 : -1)));
      });
  };
  const restoreProgress = () => {
    const audio = audioRef.current;
    if (!audio) return;
    setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    if (
      !restoredProgressRef.current &&
      Number(podcast?.progressTime || 0) > 0
    ) {
      audio.currentTime = Math.min(
        Number(podcast.progressTime),
        Number.isFinite(audio.duration)
          ? audio.duration
          : Number(podcast.progressTime),
      );
      setCurrentTime(audio.currentTime);
      restoredProgressRef.current = true;
    }
  };
  const updateTime = () => {
    const current = audioRef.current?.currentTime || 0;
    setCurrentTime(current);
    if (
      !viewStoredRef.current &&
      current >= Number(podcast?.viewCountSeconds || 0)
    ) {
      viewStoredRef.current = true;
      storePodcastTime({
        audio_podcast_id: podcast.id,
        subject_id: subjectId,
        timer_seconds: Math.max(1, Math.floor(elapsedRef.current)),
        is_view: 1,
      }).catch(() => {
        viewStoredRef.current = false;
      });
    }
  };

  return (
    <>
      <div className="spAudio__page">
        <div className="spAudio__card">
          <div className="spAudio__topActions">
            <span className="time-badge">
              <i className="bi bi-clock" />
              <span>{formatTime(elapsedSeconds)}</span>
            </span>
            <button
              type="button"
              className={`spAudio__read ${isRead ? "is-read" : ""}`}
              onClick={markRead}
            >
              <i
                className={`bi ${isRead ? "bi-check2-circle" : "bi-envelope-check"}`}
              />{" "}
              {isRead ? "Read" : "Mark Read"}
            </button>
            <button
              type="button"
              className="spAudio__topAction"
              onClick={toggleBookmark}
              aria-label="Bookmark podcast"
            >
              <i
                className={`bi ${isBookmarked ? "bi-bookmark-fill" : "bi-bookmark"}`}
              />
            </button>
            <button
              type="button"
              className="spAudio__topAction spAudio__heart"
              onClick={toggleLike}
              aria-label="Like podcast"
            >
              <i className={`bi ${isLiked ? "bi-heart-fill" : "bi-heart"}`} />
            </button>
          </div>
          {locked ? (
            <div className="spAudio__error">
              <p>
                {podcast?.subscriptionMessage ||
                  "Unlock this podcast to start listening."}
              </p>
              <button type="button" className="spAudio__unlockButton" aria-label="Play locked podcast" onClick={onPurchase}>Play</button>
              <button type="button" className="spAudio__unlockButton" onClick={onPurchase}>Subscribe</button>
              <button
                type="button"
                className="spAudio__unlockButton"
                onClick={onCoinUnlock}
              >
                Unlock with Coins
              </button>
            </div>
          ) : isVideo ? (
            <VideoPlayer lecture={{ lecture_id: podcast.id, name: podcast.title, lecture_video: podcast.mediaUrl,
              video_source: provider, lecture_file_id: String(podcast.raw?.podcast_file_id || podcast.mediaUrl).match(/(?:vimeo\.com\/(?:video\/)?|^)(\d+)/)?.[1] || '',
              previous_lecture_id: podcast.previousId, next_lecture_id: podcast.nextId }}
              resumeAt={podcast.progressTime} elapsed={elapsedSeconds}
              onProgress={seconds => { videoProgressRef.current = seconds; }}
              onPrevious={() => navigateEpisode(podcast.previousId)} onNext={() => navigateEpisode(podcast.nextId)} />
          ) : (
            <>
              <div className="spAudio__mainPlayer">
                <button
                  type="button"
                  className="spAudio__skipButton"
                  onClick={() => seekBy(-10)}
                  aria-label="Rewind 10 seconds"
                >
                  <i className="bi bi-arrow-counterclockwise" />
                  <small>10</small>
                </button>
                <button
                  type="button"
                  className="spAudio__playButton"
                  onClick={togglePlay}
                  aria-label={isPlaying ? "Pause audio" : "Play audio"}
                >
                  {isPlaying ? (
                    <i className="bi bi-pause-fill" />
                  ) : (
                    <i className="bi bi-play-fill" />
                  )}
                </button>
                <button
                  type="button"
                  className="spAudio__skipButton"
                  onClick={() => seekBy(10)}
                  aria-label="Forward 10 seconds"
                >
                  <i className="bi bi-arrow-clockwise" />
                  <small>10</small>
                </button>
              </div>
              <p className="spAudio__playHint">
                {isPlaying
                  ? "Now playing this episode"
                  : "Tap play to start this episode"}
              </p>
              <div className="spAudio__progressWrapper">
                <div className="spAudio__progressTrack" />
                <div
                  className="spAudio__progressFilled"
                  style={{ width: `${progress}%` }}
                />
                <input
                  type="range"
                  min="0"
                  max={duration || 0}
                  step="0.1"
                  value={currentTime}
                  onChange={seek}
                  className="spAudio__progressInput"
                  aria-label="Audio progress"
                />
              </div>
              <div className="spAudio__timeRow">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
              <audio
                aria-label="Podcast audio"
                ref={audioRef}
                src={podcast?.mediaUrl || ""}
                preload="metadata"
                onLoadedMetadata={restoreProgress}
                onTimeUpdate={updateTime}
                onPlay={() => setIsPlaying(true)}
                onPause={() => {
                  setIsPlaying(false);
                  saveProgress();
                }}
                onEnded={() => {
                  setIsPlaying(false);
                  markRead();
                  saveProgress();
                }}
                onError={() => setAudioError(true)}
              />
            </>
          )}
          <div className="spAudio__episodeNav">
            <button
              type="button"
              disabled={!podcast?.previousId}
              onClick={() => navigateEpisode(podcast.previousId)}
            >
              <i className="bi bi-skip-backward-fill" />
              <span>Previous</span>
              <small>
                {podcast?.previousId
                  ? podcast?.previousFreePaid === "P"
                    ? "Prime"
                    : "Free"
                  : "Unavailable"}
              </small>
            </button>
            <button
              type="button"
              disabled={!podcast?.nextId}
              onClick={() => navigateEpisode(podcast.nextId)}
            >
              <span>Next</span>
              <i className="bi bi-skip-forward-fill" />
              <small>
                {podcast?.nextId
                  ? podcast?.nextFreePaid === "P"
                    ? "Prime"
                    : "Free"
                  : "Unavailable"}
              </small>
            </button>
          </div>
          {audioError ? (
            <div className="spAudio__error">
              Unable to load the audio. Please check the audio source.
            </div>
          ) : null}
          <div className="spAudio__stats">
            <button type="button" onClick={() => onOpenModal?.("view")}>
              <i className="bi bi-eye" />
              <strong>{podcast?.viewCount || 0}</strong>
              <span>Views</span>
            </button>
            <button type="button" onClick={() => onOpenModal?.("notes")}>
              <i className="bi bi-journal-text" />
              <strong>{podcast?.notesCount || 0}</strong>
              <span>Notes</span>
            </button>
            <button type="button" onClick={() => onOpenModal?.("report")}>
              <i className="bi bi-exclamation-circle" />
              <strong>{podcast?.reportCount || "0"}</strong>
              <span>Reports</span>
            </button>
            <div>
              <i className="bi bi-heart" />
              <strong>{likeCount}</strong>
              <span>Likes</span>
            </div>
            <button type="button" onClick={markRead}>
              <i
                className={`bi ${isRead ? "bi-check-circle-fill" : "bi-circle"}`}
              />
              <strong>
                {isRead
                  ? "Read"
                  : podcast?.freePaid === "P"
                    ? "Locked"
                    : "Free"}
              </strong>
              <span>Access</span>
            </button>
          </div>
        </div>
      </div>
      {children}
    </>
  );
};

export default AudioPlayer;
